/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React from "react";

import type { TooltipProps } from "recharts";

type SalesDashboardChartTooltipProps = TooltipProps<number, string> & {
  title?: React.ReactNode;
  formatValue?: (value: number) => React.ReactNode;
  getSeriesColor?: (seriesName: string) => string | undefined;
};

export const SalesDashboardChartTooltip: React.FC<
  SalesDashboardChartTooltipProps
> = ({ active, payload, label, title, formatValue, getSeriesColor }) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  return (
    <div css={styles.container}>
      <div css={styles.header}>{title ?? label ?? ""}</div>
      <div css={styles.body}>
        {payload.map(
          (
            { color: payloadColor, dataKey, name, payload: itemPayload, value },
            index
          ) => {
            const seriesName = String(name ?? dataKey ?? "");
            const payloadValue = getPayloadValue(itemPayload, dataKey);
            const numericValue = Number(value ?? payloadValue ?? 0);
            const color =
              getSeriesColor?.(seriesName) ?? payloadColor ?? "#8b96a2";
            const formattedValue = formatValue
              ? formatValue(numericValue)
              : numericValue;

            return (
              <div css={styles.row} key={`${seriesName}-${index}`}>
                <span css={styles.marker(color)} />
                <span css={styles.name}>{seriesName}</span>
                <span css={styles.value}>{formattedValue}</span>
              </div>
            );
          }
        )}
      </div>
    </div>
  );
};

// RechartsのTooltipはチャート種別により value が空で、元データの payload[dataKey] に値が残ることがある。
const getPayloadValue = (
  payload: unknown,
  dataKey: string | number | undefined
): unknown => {
  if (
    dataKey === undefined ||
    typeof payload !== "object" ||
    payload === null
  ) {
    return undefined;
  }

  return Reflect.get(payload, dataKey);
};

const styles = {
  container: css`
    min-width: 238px;
    background: #22313f;
    border: 1px solid #2e3d4c;
    border-radius: 10px;
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.38);
    pointer-events: none;
    overflow: hidden;
  `,
  header: css`
    padding: 9px 14px;
    color: #b9c3cd;
    font-size: 12.5px;
    letter-spacing: 0.4px;
    border-bottom: 1px solid #2e3d4c;
  `,
  body: css`
    padding: 5px 14px 8px;
  `,
  row: css`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 5px 0;
  `,
  marker: (color: string) => css`
    flex: none;
    width: 11px;
    height: 11px;
    background: ${color};
    border-radius: 50%;
  `,
  name: css`
    flex: 1;
    color: #dbe6ee;
    font-size: 13px;
  `,
  value: css`
    color: #ffffff;
    font-size: 14px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  `,
};
